import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { FormControlStatus, FormGroup } from '@angular/forms';
import { CustomerSelectionOption } from '@app/customer/models/customer-selection-option.model';
import { IonNav, PopoverController } from '@ionic/angular';
import { TranslateService } from '@ngx-translate/core';
import { BankNameResponse, BankService } from '@payments/services/bank.service';
import { PaymentsModalService } from '@payments/services/payments-modal.service';
import { CustomerSelectionPaymentsFilterMobileComponent } from '@shared/components/header/customer-selection/customer-selection-payments-filter-mobile/customer-selection-payments-filter-mobile.component';
import { ValidateBankAccountResponse } from '@usf/payment-types';
import {
  combineLatest,
  distinctUntilChanged,
  filter,
  mergeMap,
  Observable,
  startWith,
  Subscription,
  tap,
  withLatestFrom,
} from 'rxjs';
import {
  CheckReferencePopoverComponent,
  ImageDetailEnum,
} from '../check-reference-popover/check-reference-popover.component';
import { CheckReferenceTabTouchComponent } from '../check-reference-tab-touch/check-reference-tab-touch.component';

@Component({
  selector: 'app-add-bank-account-form',
  templateUrl: './add-bank-account-form.component.html',
  styleUrls: ['./add-bank-account-form.component.scss'],
})
export class AddBankAccountFormComponent implements OnInit, OnDestroy {
  @Input() addBankAccountForm: FormGroup;
  @Input() canSelectCustomers: boolean;
  @Input() isDesktop: boolean;
  @Input() showIsPrimaryField = true;
  @Input() showRoutingNumberField = true;
  @Input() showNickNameField = true;
  @Input() selectedCustomers: CustomerSelectionOption[];
  @Input() canModifyForm: boolean;
  @Input() isBankRoutingValidationOn: boolean;
  @Output() isLoadingEmit: EventEmitter<boolean> = new EventEmitter();
  @Output() submitEmit: EventEmitter<void> = new EventEmitter();

  isBankNameInputEnabled = false;
  routingNumberStatusChangeSub$: Subscription;
  routingNumberErrorKey: string | null = null;
  routingNumberErrorSub$: Subscription;

  constructor(
    private nav: IonNav,
    private popoverController: PopoverController,
    private paymentsModalService: PaymentsModalService,
    private translateService: TranslateService,
    private bankService: BankService,
  ) {}

  ngOnInit() {
    this.setupFormEvents();
  }

  async openCheckReference(event: MouseEvent) {
    const eventTargetAsElement = event?.target as HTMLElement;
    const elemId = eventTargetAsElement.id;

    const imageDetail =
      elemId === 'account-number'
        ? ImageDetailEnum.accountNumber
        : ImageDetailEnum.routingNumber;

    if (this.isDesktop) {
      this.openCheckReferencePopover(event, imageDetail);
    } else {
      this.nav.push(CheckReferenceTabTouchComponent, {
        imageDetail,
        return: () => {
          this.nav.popToRoot();
        },
      });
    }
  }

  openSelectCustomers() {
    this.nav.push(CustomerSelectionPaymentsFilterMobileComponent, {
      vm$: this.paymentsModalService.getCustomerSelectionVM$(true),
      searchText$: this.paymentsModalService.searchText$,
      title: this.translateService.instant(
        'i18n.payments.addBankAccountModal.customers',
      ),
      isPresentedInFilterModal: false,
      selectedCustomers: this.selectedCustomers,
    });
  }

  async openCheckReferencePopover(event: Event, imageDetail: ImageDetailEnum) {
    const popover = await this.popoverController.create({
      component: CheckReferencePopoverComponent,
      componentProps: {
        imageDetail,
        dismissPopover: () => {
          this.dismissPopover();
        },
      },
      cssClass: 'add-bank-account-popover',
      mode: 'ios',
      showBackdrop: true,
      backdropDismiss: true,
      reference: 'event',
      event,
    });

    await popover.present();
  }

  async dismissPopover() {
    this.popoverController.dismiss();
  }

  submitBankAccountData() {
    this.submitEmit.emit();
  }

  setupFormEvents(): void {
    !this.canModifyForm && this.addBankAccountForm.disable();

    let statusChange$: Observable<
      [FormControlStatus, FormControlStatus, FormControlStatus]
    >;
    if (this.isBankRoutingValidationOn) {
      statusChange$ = combineLatest([
        this.addBankAccountForm.controls.routingNumber?.statusChanges,
        this.addBankAccountForm.controls.checkingAccountNumber?.statusChanges,
        this.addBankAccountForm.controls.confirmCheckingAccountNumber
          ?.statusChanges,
      ]);
    } else {
      statusChange$ =
        this.addBankAccountForm.controls.routingNumber?.statusChanges.pipe(
          withLatestFrom(
            this.addBankAccountForm.controls.checkingAccountNumber?.statusChanges.pipe(
              startWith('INVALID' as FormControlStatus),
            ),
            this.addBankAccountForm.controls.confirmCheckingAccountNumber?.statusChanges.pipe(
              startWith('INVALID' as FormControlStatus),
            ),
          ),
        );
    }

    this.routingNumberErrorSub$ =
      this.addBankAccountForm.controls.routingNumber?.statusChanges.subscribe(
        routingStatus => {
          if (routingStatus === 'INVALID') {
            this.routingNumberErrorKey = Object.keys(
              this.addBankAccountForm.controls.routingNumber.errors,
            )[0];
          } else {
            this.routingNumberErrorKey = null;
          }
        },
      );

    this.routingNumberStatusChangeSub$ = statusChange$
      .pipe(
        distinctUntilChanged(
          ([rPrev, cPrev, ccPrev], [rCurr, cCurr, ccCurr]) => {
            if (this.isBankRoutingValidationOn) {
              return rPrev === rCurr && cPrev === cCurr && ccPrev === ccCurr;
            } else {
              return rPrev === rCurr;
            }
          },
        ),
        tap(([routingStatus]) => {
          if (routingStatus === 'INVALID') {
            this.isBankNameInputEnabled = false;
            this.addBankAccountForm.controls.bankName?.reset();
          }
        }),
        filter(([routingStatus, checkingStatus, conrfirmStatus]) => {
          const controls = this.addBankAccountForm.controls;
          if (this.isBankRoutingValidationOn) {
            return (
              !isNaN(controls.routingNumber.value) &&
              routingStatus === 'VALID' &&
              !isNaN(controls.checkingAccountNumber.value) &&
              checkingStatus === 'VALID' &&
              !isNaN(controls.confirmCheckingAccountNumber.value) &&
              conrfirmStatus === 'VALID' &&
              controls.checkingAccountNumber.value ===
                controls.confirmCheckingAccountNumber.value
            );
          } else {
            return (
              !isNaN(this.addBankAccountForm.controls.routingNumber.value) &&
              routingStatus === 'VALID'
            );
          }
        }),
        mergeMap(() => {
          let observableResponse:
            | Observable<BankNameResponse>
            | Observable<ValidateBankAccountResponse>;
          if (this.isBankRoutingValidationOn) {
            observableResponse = this.bankService.validateRoutingNumber(
              this.addBankAccountForm.controls.routingNumber.value,
              this.addBankAccountForm.controls.checkingAccountNumber.value,
            );
            this.isLoadingEmit.emit(true);
          } else {
            observableResponse = this.bankService.getBankName(
              this.addBankAccountForm.controls.routingNumber.value,
            );
          }
          return observableResponse;
        }),
      )
      .subscribe({
        next: res => {
          if (this.isBankRoutingValidationOn) {
            if (res?.bankName) {
              this.addBankAccountForm.controls.bankName?.setValue(res.bankName);
            } else {
              this.addBankAccountForm.controls.routingNumber?.setErrors({
                invalidRoutingNumber: true,
              });
            }
            this.addBankAccountForm.controls.validationStatus?.setValue(
              (res as ValidateBankAccountResponse).accountResult.code,
            );
          } else {
            if (res?.bankName) {
              this.addBankAccountForm.controls.bankName?.setValue(res.bankName);
            } else {
              this.enableBankNameInput();
            }
          }
          this.addBankAccountForm.controls.routingNumber?.markAsTouched();
          this.isLoadingEmit.emit(false);
        },
        error: _error => {
          this.addBankAccountForm.controls.routingNumber?.markAsTouched();
          if (!this.isBankRoutingValidationOn) {
            this.enableBankNameInput();
          }
          this.isLoadingEmit.emit(false);
        },
      });
  }

  enableBankNameInput() {
    this.isBankNameInputEnabled = true;
    this.addBankAccountForm.controls.bankName?.setErrors({
      unknownRoutingNumber: true,
    });
  }

  ngOnDestroy() {
    this.routingNumberErrorSub$?.unsubscribe();
    this.routingNumberStatusChangeSub$?.unsubscribe();
  }
}
